import React from 'react';
import styled from 'styled-components';
import colors from '../theme/colors';
import { resolveStrapiMediaUrl } from '../services/api';
import ArticlesWithFilter from './ArticlesWithFilter';
import Slider from './Slider';

const RichTextBlock = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: var(--font-size-h1, 2.25rem);
    margin: 2rem 0 1rem 0;
    color: ${colors.mainBlack};
    font-weight: 700;
    line-height: 1.25;
  }

  h2 {
    font-size: var(--font-size-h2, 1.75rem);
    margin: 2rem 0 0.75rem 0;
    color: ${colors.mainBlack};
    font-weight: 600;
    line-height: 1.3;
  }

  h3 {
    font-size: var(--font-size-h3, 1.375rem);
    margin: 1.5rem 0 0.5rem 0;
    color: ${colors.mainBlack};
    font-weight: 600;
    line-height: 1.35;
  }

  h4, h5, h6 {
    font-size: var(--font-size-h4, 1.125rem);
    margin: 1.25rem 0 0.5rem 0;
    color: ${colors.mainBlack};
    font-weight: 600;
  }

  p {
    margin-bottom: 1.25rem;
    line-height: 1.7;
    color: ${colors.textDark};
    font-size: 1.0625rem;
  }

  ul, ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.7;
    color: ${colors.textDark};
  }

  a {
    color: ${colors.mainOrange};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: ${colors.mainOrangeLight};
      text-decoration: underline;
    }
  }

  strong {
    font-weight: 600;
    color: ${colors.mainBlack};
  }

  em {
    font-style: italic;
  }
`;

const QuoteBlock = styled.blockquote`
  margin: 2rem 0;
  padding: 1.5rem 2rem;
  background: ${colors.goldGrey};
  border-left: 4px solid ${colors.mainOrange};
  border-radius: 4px;
  font-style: italic;
  color: ${colors.mainBlack};

  p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.6;
  }

  cite {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: ${colors.heavyGray};
    font-style: normal;
    font-weight: 500;
  }
`;

const MediaImageWrap = styled.div`
  border-radius: 10px;
  overflow: hidden;
  line-height: 0;
  font-size: 0;
  box-shadow: 0 2px 12px rgba(30, 30, 30, 0.1);
  border: 1px solid ${colors.goldGrey};
  background: ${colors.mainWhite};

  img {
    width: 100%;
    height: auto;
    display: block;
    vertical-align: top;
  }
`;

const MediaBlock = styled.figure`
  margin: 1.75rem 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;

  > ${MediaImageWrap} {
    width: 100%;
  }
`;

const MediaCaption = styled.figcaption`
  margin: 0;
  padding: 0.35rem 0.25rem 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${colors.textGray};
  font-style: italic;
  text-align: center;
`;

interface Block {
  __component: string;
  body?: any;
  title?: string;
  file?: any;
  files?: any[];
  showAllOption?: boolean;
}

interface NormalizedMedia {
  url: string;
  alt?: string;
  /** Підпис з Media Library (Strapi upload: caption) */
  caption?: string;
}

/** Якщо `url` порожній, беремо перший доступний з `formats` (типовий вивід Strapi для зображень). */
function pickRawMediaUrl(m: any): string {
  if (!m || typeof m !== 'object') return '';
  const direct = m.url;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const formats = m.formats;
  if (formats && typeof formats === 'object') {
    const order = ['large', 'medium', 'small', 'thumbnail', 'xsmall', 'xlarge'];
    for (const k of order) {
      const u = formats[k]?.url;
      if (typeof u === 'string' && u.trim()) return u.trim();
    }
    for (const v of Object.values(formats)) {
      const u = (v as { url?: string })?.url;
      if (typeof u === 'string' && u.trim()) return u.trim();
    }
  }
  return '';
}

function unwrapStrapiValue(value: any): any {
  if (value == null) return null;
  if (Array.isArray(value)) return value.map(unwrapStrapiValue);
  if (typeof value !== 'object') return value;
  if ('data' in value) return unwrapStrapiValue(value.data);
  if ('attributes' in value && value.attributes && typeof value.attributes === 'object') {
    return { ...value, ...value.attributes };
  }
  return value;
}

function normalizeMedia(media: any): NormalizedMedia | null {
  const m = unwrapStrapiValue(media);
  if (!m || typeof m !== 'object') return null;
  const raw = pickRawMediaUrl(m);
  const url = raw ? resolveStrapiMediaUrl(raw) : '';
  if (!url) return null;
  const cap = m.caption;
  const caption = typeof cap === 'string' && cap.trim() ? cap.trim() : undefined;
  return {
    url,
    alt: m.alternativeText || m.name || m.alt || 'Зображення статті',
    caption,
  };
}

function normalizeMediaList(files: any): NormalizedMedia[] {
  const unwrapped = unwrapStrapiValue(files);
  const list = Array.isArray(unwrapped) ? unwrapped : unwrapped ? [unwrapped] : [];
  return list
    .map((item) => normalizeMedia(item))
    .filter(Boolean) as NormalizedMedia[];
}

interface BlocksRendererProps {
  blocks: Block[];
}

const BlocksRenderer: React.FC<BlocksRendererProps> = ({ blocks }) => {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  const renderBlock = (block: Block, index: number) => {
    switch (block.__component) {
      case 'shared.rich-text':
        return (
          <RichTextBlock key={index}>
            {renderRichTextBody(block.body, `rt-${index}`)}
          </RichTextBlock>
        );

      case 'shared.quote':
        return (
          <QuoteBlock key={index}>
            {block.body && <p>{block.body}</p>}
            {block.title && <cite>— {block.title}</cite>}
          </QuoteBlock>
        );

      case 'shared.media':
        const mediaItem = normalizeMedia((block as any).file ?? (block as any).media);
        return (
          <MediaBlock key={index}>
            {mediaItem ? (
              <>
                <MediaImageWrap>
                  <img
                    src={mediaItem.url}
                    alt={mediaItem.alt || 'Зображення статті'}
                  />
                </MediaImageWrap>
                {mediaItem.caption ? <MediaCaption>{mediaItem.caption}</MediaCaption> : null}
              </>
            ) : (
              <p>Зображення недоступне</p>
            )}
          </MediaBlock>
        );

      case 'shared.slider':
        const sliderImages = normalizeMediaList((block as any).files ?? (block as any).images);
        if (sliderImages.length === 0) return <p key={index}>У слайдері немає зображень</p>;
        return <Slider key={index} images={sliderImages} />;

      case 'shared.articles-filter':
        return (
          <ArticlesWithFilter
            key={index}
            showAllOption={block.showAllOption !== undefined ? block.showAllOption : true}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {blocks.map((block, index) => renderBlock(block, index))}
    </>
  );
};

// Простий конвертер Markdown в HTML (fallback для старих даних)
function convertMarkdownToHtml(markdown: string): string {
  let html = markdown;

  // Заголовки
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Жирний текст
  html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/gim, '<strong>$1</strong>');

  // Курсив
  html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
  html = html.replace(/_(.*?)_/gim, '<em>$1</em>');

  // Посилання
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>');

  // Списки
  html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
  html = html.replace(/^\+ (.*$)/gim, '<li>$1</li>');
  html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');

  // Обгортаємо списки в <ul>
  html = html.replace(/(<li>[\s\S]*<\/li>)/, '<ul>$1</ul>');

  // Параграфи
  html = html.split('\n\n').map(paragraph => {
    if (paragraph.trim() && !paragraph.match(/^<[h|u|o|l]/)) {
      return `<p>${paragraph.trim()}</p>`;
    }
    return paragraph;
  }).join('\n');

  // Видаляємо зайві переноси
  html = html.replace(/\n/g, ' ');

  return html;
}

function renderRichTextBody(body: any, keyPrefix: string): React.ReactNode {
  if (!body) return null;

  // Legacy markdown string
  if (typeof body === 'string') {
    return <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(body) }} />;
  }

  // Strapi blocks editor JSON
  if (Array.isArray(body)) {
    return body.map((node, i) => renderRichNode(node, `${keyPrefix}-${i}`));
  }

  return null;
}

function renderRichNode(node: any, key: string): React.ReactNode {
  if (!node || typeof node !== 'object') return null;

  if (node.type === 'paragraph') {
    return <p key={key}>{renderNodeChildren(node.children, `${key}-p`)}</p>;
  }

  if (node.type === 'heading') {
    const level = Number(node.level) || 2;
    const safeLevel = Math.min(6, Math.max(1, level));
    const Tag = `h${safeLevel}` as React.ElementType;
    return <Tag key={key}>{renderNodeChildren(node.children, `${key}-h`)}</Tag>;
  }

  if (node.type === 'list') {
    const ListTag = node.format === 'ordered' ? 'ol' : 'ul';
    return (
      <ListTag key={key}>
        {Array.isArray(node.children)
          ? node.children.map((item: any, idx: number) => (
              <li key={`${key}-li-${idx}`}>{renderNodeChildren(item.children, `${key}-li-${idx}`)}</li>
            ))
          : null}
      </ListTag>
    );
  }

  if (node.type === 'quote') {
    return (
      <QuoteBlock key={key}>
        <p>{renderNodeChildren(node.children, `${key}-q`)}</p>
      </QuoteBlock>
    );
  }

  if (node.type === 'code') {
    return (
      <pre key={key}>
        <code>{renderNodeChildren(node.children, `${key}-code`)}</code>
      </pre>
    );
  }

  if (node.type === 'image') {
    const imageNodeMedia = normalizeMedia(unwrapStrapiValue(node.image) || node);
    if (!imageNodeMedia) return null;
    return (
      <MediaBlock key={key}>
        <MediaImageWrap>
          <img src={imageNodeMedia.url} alt={imageNodeMedia.alt || 'Зображення статті'} />
        </MediaImageWrap>
        {imageNodeMedia.caption ? <MediaCaption>{imageNodeMedia.caption}</MediaCaption> : null}
      </MediaBlock>
    );
  }

  // Unknown container node: render children recursively
  return <React.Fragment key={key}>{renderNodeChildren(node.children, `${key}-children`)}</React.Fragment>;
}

function renderNodeChildren(children: any, keyPrefix: string): React.ReactNode {
  if (!Array.isArray(children)) return null;
  return children.map((child: any, idx: number) => renderInlineOrNode(child, `${keyPrefix}-${idx}`));
}

function renderInlineOrNode(node: any, key: string): React.ReactNode {
  if (!node || typeof node !== 'object') return null;

  if (node.type === 'text') {
    let content: React.ReactNode = node.text || '';
    if (node.bold) content = <strong key={`${key}-b`}>{content}</strong>;
    if (node.italic) content = <em key={`${key}-i`}>{content}</em>;
    if (node.underline) content = <u key={`${key}-u`}>{content}</u>;
    if (node.strikethrough) content = <s key={`${key}-s`}>{content}</s>;
    if (node.code) content = <code key={`${key}-c`}>{content}</code>;
    return <React.Fragment key={key}>{content}</React.Fragment>;
  }

  if (node.type === 'link') {
    const href = typeof node.url === 'string' ? node.url : '';
    const external = /^https?:\/\//i.test(href);
    return (
      <a
        key={key}
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {renderNodeChildren(node.children, `${key}-a`)}
      </a>
    );
  }

  if (node.type === 'linebreak') {
    return <br key={key} />;
  }

  return renderRichNode(node, key);
}

export default BlocksRenderer;

